// ── Client branches ──────────────────────────────────────────────────────────

export interface ClientBranch {
  id: string;
  client: string;
  client_name: string;
  name: string;
  street: string;
  postal_code: string;
  city: string;
  display_name: string;
  contact_person: string;
  contact_email: string;
  contact_phone: string;
  notes: string;
  is_active: boolean;
  /** Etykiety aktywnych flag „szczególnej opieki" (karta specyfiki). */
  care_labels?: string[];
  created_at: string;
  updated_at: string;
}

export interface ClientSubBranch {
  id: string;
  branch: string;
  branch_name: string;
  client_name: string;
  name: string;
  postal_code: string;
  city: string;
  display_name: string;
  contact_person: string;
  contact_email: string;
  contact_phone: string;
  notes: string;
  is_active: boolean;
  /** Etykiety aktywnych flag „szczególnej opieki" (karta specyfiki). */
  care_labels?: string[];
  created_at: string;
  updated_at: string;
}

// ── Similar proposals ────────────────────────────────────────────────────────

export interface SimilarProposalItem {
  template_id: string;
  name: string;
  in_current: boolean;
  in_past: boolean;
  past_quantity: string | null;
  past_base_rate: string | null;
  past_line_total: string | null;
}

export interface PricingAdviceFactor {
  name: string;
  weight: "high" | "medium" | "low";
  effect: string;
}

export interface PricingAdvice {
  headline: string;
  direction: "up" | "down" | "hold";
  reasoning: string[];
  factors: PricingAdviceFactor[];
  confidence: "high" | "medium" | "low";
  caveat?: string;
}

export interface PricingCurvePoint {
  multiplier: number;
  win_prob: number;
  expected_margin: number;
}

export interface PricingModelFactor {
  factor: string;
  segment: string | null;
  segment_win_rate: number | null;
  segment_won: number;
  segment_n: number;
  segment_median_multiplier: number | null;
  low_sample: boolean;
  /** Measured importance of the dimension across the portfolio (best − worst win-rate). */
  portfolio_spread: number;
  /** Business prior on top of the measured importance — rejon 5.0, every other factor 1.0. */
  prior_weight?: number;
  /** Weight the model actually used = prior × spread × reliability. Factors are sorted by it. */
  weight?: number;
  best_segment: string | null;
  worst_segment: string | null;
  log_odds: number;
  sentiment: "up" | "down" | "neutral";
}

export interface PricingEntryBasis {
  factor: string;
  segment: string | null;
  segment_won: number;
  segment_n: number;
  median_multiplier: number;
}

export interface PricingModel {
  curve: PricingCurvePoint[];
  curve_min: number;
  curve_max: number;
  optimum: PricingCurvePoint | null;
  /** Conservative, envelope-capped recommendation — prefer this over `optimum` in the UI. */
  recommended: PricingCurvePoint | null;
  recommended_capped: boolean;
  current: PricingCurvePoint | null;
  /** Typical multiplier we historically bid on this profile ("próg wyjściowy"). */
  entry_threshold: number | null;
  observed_lo: number | null;
  observed_hi: number | null;
  entry_basis: PricingEntryBasis | null;
  factors: PricingModelFactor[];
  cost_pct: number;
  base_subtotal: number;
  overall_win_rate: number;
  overall_won: number;
  overall_total: number;
  portfolio_resolved: number;
  fit_n: number;
  price_slope: number;
  low_confidence: boolean;
}

export interface PricingModelResponse {
  model: PricingModel | null;
}

export interface PricingAdvicePayload {
  tender_id: string;
  cost_pct: number;
  base_subtotal: number;
  current_multiplier: number;
}

export interface PricingAdviceResponse {
  advice: PricingAdvice | null;
  available: boolean;
  model?: PricingModel | null;
  cost_usd?: string;
  tokens?: { input: number; output: number; cache_read: number; cache_creation: number };
}

export interface SimilarProposal {
  score: number;
  similarity: number;
  tender: {
    id: string;
    title: string;
    status: TenderStatus;
    status_display: string;
    client_name: string;
    client_branch_name: string | null;
    same_branch: boolean;
    won_at: string | null;
    created_at: string;
  };
  proposal: {
    id: string;
    value: string;
    multiplier: string;
    notes: string;
  };
  winning_offer_value: string | null;
  common_count: number;
  new_count: number;
  removed_count: number;
  /** Zgodność nazw plików dokumentacji — podnosi trafność, nigdy jej nie obniża.
   *  0 znaczy „inne nazwy ALBO żaden z przetargów nie ma wgranych plików". */
  shared_files: number;
  file_similarity: number;
  shared_file_names: string[];
  items: SimilarProposalItem[];
}

// "not_starting" ("Nie startujemy") is a soft-delete state: we decided not to bid.
// Such tenders are hidden from the default list (backend excludes them unless the
// status filter explicitly asks for them) and can be restored back to "open".
export type TenderStatus =
  | "open"
  | "won_unofficial"
  | "won"
  | "lost"
  | "cancelled"
  | "not_starting";
export type TenderRoundStatus = "active" | "submitted" | "completed";
export type PricingProposalStatus = "pending" | "accepted" | "rejected";
export type TenderMessageMarkType = "important" | "question" | "action" | "decision";

export const TENDER_STATUS_LABELS: Record<TenderStatus, string> = {
  open: "Otwarte",
  won_unofficial: "Wygrany - nieoficjalnie",
  won: "Wygrane",
  lost: "Przegrane",
  cancelled: "Anulowane",
  not_starting: "Nie startujemy",
};

export const TENDER_STATUS_COLORS: Record<
  TenderStatus,
  "blue" | "green" | "red" | "gray" | "yellow"
> = {
  open: "blue",
  // Soft win (not yet officially confirmed) — amber, distinct from the official green
  // win; signals "win pending official confirmation".
  won_unofficial: "yellow",
  won: "green",
  lost: "red",
  cancelled: "gray",
  not_starting: "gray",
};

export const PRICING_PROPOSAL_STATUS_LABELS: Record<PricingProposalStatus, string> = {
  pending: "Oczekuje",
  accepted: "Zaakceptowana",
  rejected: "Odrzucona",
};

export const MESSAGE_MARK_LABELS: Record<TenderMessageMarkType, string> = {
  important: "Ważne",
  question: "Pytanie",
  action: "Do działania",
  decision: "Decyzja",
};

export const MESSAGE_MARK_COLORS: Record<TenderMessageMarkType, string> = {
  important: "text-red-600 bg-red-50",
  question: "text-blue-600 bg-blue-50",
  action: "text-orange-600 bg-orange-50",
  decision: "text-purple-600 bg-purple-50",
};

export interface TenderAttachment {
  id: string;
  name: string;
  file: string;
  file_url: string | null;
  uploaded_by: string;
  uploaded_by_name: string;
  created_at: string;
}

export interface TenderMaterialFile {
  id: string;
  name: string;
  file: string;
  file_url: string | null;
  uploaded_by: string;
  uploaded_by_name: string;
  created_at: string;
}

export interface TenderMessageAttachment {
  id: string;
  name: string;
  file_url: string | null;
  content_type: string;
  is_image: boolean;
  created_at: string;
}

export interface TenderMessage {
  id: string;
  tender: string;
  author: string;
  author_name: string;
  content: string;
  is_marked: boolean;
  mark_type: TenderMessageMarkType | null;
  parent: string | null;
  replies: TenderMessage[];
  attachments: TenderMessageAttachment[];
  created_at: string;
  updated_at: string;
}

export interface TenderOffer {
  id: string;
  tender_round: string;
  value: string;
  file: string | null;
  file_url: string | null;
  notes: string;
  is_withdrawn: boolean;
  submitted_by: string;
  submitted_by_name: string;
  created_at: string;
}

export interface PricingNotification {
  id: string;
  recipient: string;
  recipient_name: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

export interface TenderPricingProposalItem {
  id: string;
  source_template: string | null;
  source_template_name: string | null;
  name: string;
  base_rate: string;
  quantity: string;
  /** False — item is counted 1:1, without the pricing multiplier. */
  apply_multiplier: boolean;
  order: number;
  line_total: string;
  /** Current rate of the source template (may differ from snapshotted base_rate). */
  current_rate: string | null;
  /** True when the template's rate has changed since this proposal item was created. */
  rate_changed: boolean;
}

export interface TenderPricingProposal {
  id: string;
  tender_round: string;
  proposed_by: string;
  proposed_by_name: string;
  value: string;
  multiplier: string;
  subtotal: string;
  notes: string;
  status: PricingProposalStatus;
  reviewed_by: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  notifications: PricingNotification[];
  items: TenderPricingProposalItem[];
  created_at: string;
  updated_at: string;
}

export interface TenderRound {
  id: string;
  tender: string;
  round_number: number;
  offer_deadline: string | null;
  effective_deadline: string;
  status: TenderRoundStatus;
  notes: string;
  offers: TenderOffer[];
  pricing_proposals: TenderPricingProposal[];
  /** Kto przejął wyliczanie wyceny tej tury (claim-pricing); null = nikt. */
  pricing_claimed_by: string | null;
  pricing_claimed_by_name: string | null;
  pricing_claimed_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Co przetarg obejmuje. "" = nie ustalono z nazwy i wymaga człowieka — celowo NIE jest
 *  domyślnie „projekt", bo zakres decyduje, jakie przedsięwzięcie powstaje po wygranej. */
export type TenderWorkScope =
  | ""
  | "projekt"
  | "wykonawstwo"
  | "projekt_i_wykonawstwo";

export const WORK_SCOPE_LABEL: Record<Exclude<TenderWorkScope, "">, string> = {
  projekt: "Projekt",
  wykonawstwo: "Wykonawstwo (budowa)",
  projekt_i_wykonawstwo: "Projekt i wykonawstwo",
};

/** Wpis w edytowalnej tabeli prefiksów: „P+W" → projekt i wykonawstwo. */
export interface TenderScopePrefix {
  id: string;
  prefix: string;
  /** Klucz porównawczy liczony na backendzie: „P + W", „p/w" i „PiW" dają to samo „PW". */
  normalized: string;
  work_scope: Exclude<TenderWorkScope, "">;
  work_scope_display: string;
  note: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TenderScopePrefixFormData {
  prefix: string;
  work_scope: Exclude<TenderWorkScope, "">;
  note?: string;
  is_active?: boolean;
}

export interface Tender {
  id: string;
  title: string;
  reference_number: string;
  source_url: string;
  client: string;
  client_name: string;
  client_branch: string | null;
  client_branch_name: string | null;
  client_sub_branch: string | null;
  client_sub_branch_name: string | null;
  description: string;
  offer_deadline: string;
  status: TenderStatus;
  status_display: string;
  work_scope: TenderWorkScope;
  work_scope_display: string;
  work_scope_source: string;
  created_by: string;
  created_by_name: string;
  won_at: string | null;
  won_unofficial_at: string | null;
  contract_file: string | null;
  contract_file_url: string | null;
  coordinator: string | null;
  coordinator_name: string | null;
  linked_project: string | null;
  rounds: TenderRound[];
  attachments: TenderAttachment[];
  material_files: TenderMaterialFile[];
  messages: TenderMessage[];
  latest_offer_value: string | null;
  /** Kanoniczna "wartość przetargu" — oferta z ostatniej rundy (niewycofana). Inna
   *  reguła niż latest_offer_value (najnowsza oferta ze wszystkich rund po created_at). */
  final_value: string | null;
  /** Wartość umowy wygranego przetargu (Project.total_value wpisany przy mark-won).
   *  Pozwala odczytać cenę bez pobierania PDF-a umowy. */
  contract_value: string | null;
  action_status: TenderActionStatus;
  /** Kto przejął wycenę aktywnej tury — tylko gdy action_status === "pricing_claimed". */
  pricing_claimed_by: string | null;
  pricing_claimed_by_name: string | null;
  is_watching: boolean;
  /** True when this tender is also a ZE tender (shares the same PK); drives the
   *  /tenders/:id → /ze-tenders/:id redirect. */
  is_ze_tender: boolean;
  created_at: string;
  updated_at: string;
}

export type TenderActionStatus =
  | "needs_pricing"
  | "pricing_claimed"
  | "needs_approval"
  | "needs_offer"
  | "offer_submitted"
  | "needs_result"
  | null;

export const ACTION_STATUS_LABELS: Record<NonNullable<TenderActionStatus>, string> = {
  needs_pricing: "Czeka na wyliczenie wyceny",
  pricing_claimed: "Przejęte",
  needs_approval: "Czeka na akceptację wyceny",
  needs_offer: "Czeka na złożenie",
  offer_submitted: "Oferta złożona",
  needs_result: "Wymaga rozstrzygnięcia",
};

export const ACTION_STATUS_COLORS: Record<NonNullable<TenderActionStatus>, string> = {
  needs_pricing: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
  pricing_claimed: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
  needs_approval: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  needs_offer: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  offer_submitted: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  needs_result: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
};

const ACTION_STATUS_SORT: Record<NonNullable<TenderActionStatus>, number> = {
  needs_pricing: 1,
  pricing_claimed: 2,
  needs_approval: 3,
  needs_offer: 4,
  needs_result: 5,
  offer_submitted: 6,
};

export function tenderActionSortKey(s: TenderActionStatus): number {
  return s ? ACTION_STATUS_SORT[s] : 7;
}

/** Etykieta badge'a „Do zrobienia" z osobą w nawiasie: „Przejęte (Jan Kowalski)",
 *  „Czeka na akceptację wyceny (MJ)". Bez osoby zwraca samą etykietę. */
export function tenderActionLabel(
  status: NonNullable<TenderActionStatus>,
  person?: string | null,
): string {
  const label = ACTION_STATUS_LABELS[status];
  return person ? `${label} (${person})` : label;
}

export interface TenderListItem {
  id: string;
  title: string;
  reference_number: string;
  source_url: string;
  client: string;
  client_name: string;
  client_branch: string | null;
  client_branch_name: string | null;
  client_sub_branch: string | null;
  client_sub_branch_name: string | null;
  offer_deadline: string;
  status: TenderStatus;
  status_display: string;
  work_scope: TenderWorkScope;
  work_scope_display: string;
  rounds_count: number;
  latest_offer_value: string | null;
  /** Value of the newest non-rejected pricing proposal (wariant wyceny). Shown
   *  grayed-out in the offer column as a fallback when there's no submitted offer. */
  latest_pricing_value: string | null;
  /** Wartość umowy wygranego przetargu (Project.total_value z mark-won) — fallback
   *  kolumny wartości, gdy wygrana nie przeszła przez rundy ofertowe. */
  contract_value: string | null;
  action_status: TenderActionStatus;
  /** Kto przejął wycenę aktywnej tury — tylko gdy action_status === "pricing_claimed". */
  pricing_claimed_by: string | null;
  pricing_claimed_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface MyPricingNotification {
  id: string;
  tender_id: string;
  tender_title: string;
  proposal_id: string;
  proposal_value: string;
  round_number: number;
  sender_name: string;
  message: string;
  is_read: boolean;
  is_pinned: boolean;
  created_at: string;
}

// ── Pricing Catalog ──────────────────────────────────────────────────────────

export interface PricingItemTemplate {
  id: string;
  client: string | null;
  client_name: string | null;
  name: string;
  base_rate: string;
  unit: string;
  notes: string;
  /** False — item is counted 1:1, without the pricing multiplier. */
  apply_multiplier: boolean;
  is_active: boolean;
  order: number;
  created_at: string;
  updated_at: string;
}

export type PresetQuantityType = "fixed" | "formula" | "function" | "manual";

export interface PricingPresetEntry {
  id: string;
  template: string | null;
  template_name: string | null;
  template_base_rate: string | null;
  template_unit: string | null;
  custom_name: string;
  quantity_type: PresetQuantityType;
  fixed_quantity: string | null;
  formula_parameter: string;
  formula_divisor: string | null;
  formula_offset: string | null;
  function: string | null;
  function_name: string | null;
  function_created_by_name: string | null;
  function_param_map: Record<string, string>;
  order: number;
}

// A global, user-authored Python function for preset entry quantities.
// Code is a single `def funkcja_presetu(parametr=0)`, run as max(0, funkcja_presetu(parametr)).
export type PresetFunctionStatus = "pending" | "approved" | "rejected";

export interface PricingPresetFunction {
  id: string;
  name: string;
  code: string;
  param_names: string[];
  is_active: boolean;
  status: PresetFunctionStatus;
  status_display: string;
  reviewed_by: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  created_by: string;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface PricingPresetFunctionFormData {
  name: string;
  code: string;
  param_names: string[];
}

export interface PricingPresetParameter {
  id: string;
  key: string;
  label: string;
  unit: string;
  default_value: string | null;
  order: number;
}

export interface PricingPreset {
  id: string;
  client: string | null;
  client_name: string | null;
  name: string;
  description: string;
  is_active: boolean;
  parameters: PricingPresetParameter[];
  entries: PricingPresetEntry[];
  created_at: string;
  updated_at: string;
}

export interface ResolvedPresetItem {
  entry_id: string;
  template_id: string | null;
  name: string;
  base_rate: string;
  unit: string;
  notes: string;
  quantity: string | null;
  quantity_type: PresetQuantityType;
  /** False — item is counted 1:1, without the pricing multiplier. */
  apply_multiplier: boolean;
}

// Form types for catalog management
export interface PricingItemTemplateFormData {
  client?: string | null;
  name: string;
  base_rate: string;
  unit: string;
  notes?: string;
  apply_multiplier?: boolean;
  order?: number;
}

export interface PricingPresetParameterFormData {
  key: string;
  label: string;
  unit?: string;
  default_value?: string | null;
  order?: number;
}

export interface PricingPresetEntryFormData {
  template?: string | null;
  custom_name?: string;
  quantity_type: PresetQuantityType;
  fixed_quantity?: string | null;
  formula_parameter?: string;
  formula_divisor?: string | null;
  formula_offset?: string | null;
  function?: string | null;
  function_param_map?: Record<string, string>;
  order?: number;
}

export interface PricingPresetFormData {
  client?: string | null;
  name: string;
  description?: string;
  parameters: PricingPresetParameterFormData[];
  entries: PricingPresetEntryFormData[];
}

// Form types
export interface TenderFormData {
  title: string;
  reference_number: string;
  source_url: string;
  client: string;
  client_branch?: string | null;
  client_sub_branch?: string | null;
  description: string;
  offer_deadline: string;
  /** Ręczna korekta zakresu. Wysłanie tego pola oznacza backendowi „człowiek
   *  zdecydował" — od tej pory parser prefiksu już go nie nadpisze. */
  work_scope?: Exclude<TenderWorkScope, "">;
}

export interface TenderRoundFormData {
  offer_deadline?: string;
  notes?: string;
}

export interface TenderOfferFormData {
  value: string;
  notes?: string;
  file?: File | null;
}

export interface TenderPricingProposalItemFormData {
  name: string;
  base_rate: string;
  quantity: string;
  /** ID of the source PricingItemTemplate — for traceability only, price is always snapshotted. */
  template_id?: string | null;
  /** False — item is counted 1:1, without the pricing multiplier. */
  apply_multiplier?: boolean;
}

export interface TenderPricingProposalFormData {
  value?: string;
  notes?: string;
  multiplier?: number;
  items?: TenderPricingProposalItemFormData[];
  /** Soft link to the ZE drawing variant the pricing was imported from — powers ZE analytics. */
  ze_variant_id?: string | null;
}

export interface MarkTenderWonFormData {
  coordinator_id: string;
  project_name: string;
  project_short_name: string;
  planned_revenue?: string | null;
  planned_revenue_date?: string | null;
  contract_file?: File | null;
  branch_name?: string;
  sub_branch_name?: string;
}
